import { useState, useRef } from "react";
import { GoabSpacer } from "@abgov/react-components";
import { useGoogleMaps } from "../../common/GoogleMapsContext";
import { AddressSearch, Declaration, ManualAddress, Map } from "./components";
import { MAP_PINDROP_TRACKING_FIELD, SEARCH_ADDRESS_COUNT_TRACKING_FIELD } from "../../utils/analytics-constants";
import { handleGA } from "../../utils/analytics";
import { useAnalyticsStep } from "../../utils/useAnalyticsStep";

export const initializeMapLocationFields = (fields, data) => {
    fields['addressNotFound'] = data['addressNotFound'];
    fields['mapLat'] = data['mapLat'];
    fields['mapLng'] = data['mapLng'];
    fields['location'] = data['location'];
    fields['acceptDeclaration'] = data['acceptDeclaration'];
}

const DEFAULT_CENTER = {
    lat: 53.5461, // Default latitude for Alberta, Canada
    lng: -113.4938, // Default longitude for Alberta, Canada
};

const MapLocation = ({ form, getResourceValue, setFormData, disabled = false, gaTracking }) => {
    const [geocodingLoading, setGeocodingLoading] = useState(false);
    
    const { isLoaded, loadError } = useGoogleMaps();

    const formCoordinates = {
        lat: form.values.mapLat,
        lng: form.values.mapLng
    }
    const hasValidCoordinates = formCoordinates.lat && formCoordinates.lng;

    const mapReference = useRef(null);

    const [mapPosition, setMapPosition] = useState(hasValidCoordinates ? formCoordinates : DEFAULT_CENTER);
    const [markerPosition, setMarkerPosition] = useState(hasValidCoordinates ? formCoordinates : null);
    const [mapInstance, setMapInstance] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(Boolean(hasValidCoordinates));
    const currentStep = useAnalyticsStep();

    const handlePinDropWithAnalytics = (location) => {
        setMarkerPosition(location);

        if (gaTracking) {
            const config = gaTracking.find(t => t.trackingField === MAP_PINDROP_TRACKING_FIELD);
            if (config) {
                handleGA({ gaTracking: [config] }, currentStep);
            }
        }
    };

    const handlePopulate = async (address, form, getResourceValue) => {
        if (gaTracking) {
            const config = gaTracking.find(t => t.trackingField === SEARCH_ADDRESS_COUNT_TRACKING_FIELD);
            if (config) {
                handleGA({ gaTracking: [config] }, currentStep);
            }
        }
        const { lat, lng, provinceName, viewport } = address;
        setGeocodingLoading(true);
        form.setFieldError("mapLat", undefined);
        // form.setFieldError("enterAlbertaAddress", undefined);
        form.setFieldValue("acceptDeclaration", false);
        form.setFieldValue('location', "");
        setSelectedAddress(false);
        // if (!provinceName || provinceName === "Alberta") {
            // Here, we set marker and map positions directly.
        if (!form.values.addressNotFound) {
            form.setFieldValue('mapLat', address.viewport.low.latitude);
            form.setFieldValue('mapLng', address.viewport.low.longitude);
        }
        setMarkerPosition(null)
        if (address.viewport && mapInstance) {
            const bounds = {
                south: viewport.low.latitude,
                west: viewport.low.longitude,
                north: viewport.high.latitude,
                east: viewport.high.longitude
            };
            mapInstance.fitBounds(bounds);
        } 
        else if (mapInstance) {
            // Fallback if no viewport: just center the map
            setMapPosition({ lat: lat, lng: lng });
        }

        // 3. SCROLL TO MAP
        mapReference.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
        setGeocodingLoading(false);
        // } else {
        //     // Handle non-Albertan addresses
        //     if (form && form.setFieldError) {
        //         setGeocodingLoading(false);
        //         setMarkerPosition(null);
        //         setSelectedAddress(false);
        //         form.setFieldValue('location', "");
        //         form.setFieldValue('mapLat', undefined);
        //         form.setFieldValue('mapLng', undefined);
        //         form.setFieldValue('acceptDeclaration', false);
        //         form.setFieldError("enterAlbertaAddress", "RESOURCES@form.location.errorEnterAlbertaAddress");
        //     }
        // }
    };

    const handleChange = (form, name, value) => {
        form.setFieldValue(name, value);

        // Additional logic for specific fields
        if (name === 'location') {
            form.setFieldValue('acceptDeclaration', false);
            if (value.length) {
                setSelectedAddress(true);
            } else {
                setSelectedAddress(false);
            }
        }

        if (name === 'addressNotFound') {
            setMarkerPosition(null); // Clear the Google Map marker
            form.setFieldValue('location', ""); // Clear the location field in the form
            form.setFieldValue('mapLat', undefined); // Clear the latitude
            form.setFieldValue('mapLng', undefined); // Clear the longitude
            form.setFieldValue('acceptDeclaration', false); // Clear the accept declaration checkbox
            form.setFieldError('mapLng', undefined);
            form.setFieldError('mapLat', undefined);
            // form.setFieldError('enterAlbertaAddress', undefined);
            form.setFieldError('acceptDeclaration', undefined);
            setSelectedAddress(false);
        }

        // Remove any errors for the updated field
        if (form.errors[name]) {
            form.setFieldError(name, undefined);
        }
    };
    
    if (!isLoaded) return <div>Loading map...</div>;
    if (loadError) return <div>Map failed to load</div>;

    return (
    <>
        <AddressSearch 
            form={form}
            getResourceValue={getResourceValue}
            handlePopulate={handlePopulate}
            disabled={disabled}
            handleChange={handleChange}
        />
        
        <p className="secondary">{getResourceValue({ key: "RESOURCES@step.label" })} 2</p>
        <GoabSpacer vSpacing="2xs" />
        
        {form.values.addressNotFound && (
            <ManualAddress 
                form={form}
                getResourceValue={getResourceValue}
                handleChange={handleChange}
            />
        )}
        
        {!form.values.addressNotFound && (
            <Map
                form={form}
                getResourceValue={getResourceValue}
                mapReference={mapReference}
                mapPosition={mapPosition}
                markerPosition={markerPosition}
                setMarkerPosition={handlePinDropWithAnalytics}
                disabled={disabled}
                setGeocodingLoading={setGeocodingLoading}
                geocodingLoading={geocodingLoading}
                setMapInstance={setMapInstance}
                setSelectedAddress={setSelectedAddress}
            />
        )}
        
        <Declaration
            form={form}
            getResourceValue={getResourceValue}
            handleChange={handleChange}
            markerPosition={markerPosition}
            selectedAddress={selectedAddress}
        />
    </>
);
}

export default MapLocation;